import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocalParticipant, useTracks } from '@livekit/components-react';
import {
  Track,
  VideoQuality,
  type LocalParticipant,
  type LocalTrackPublication,
  type LocalVideoTrack,
} from 'livekit-client';
import { useRoom } from '../room/RoomProvider';
import { DEFAULT_HI_RES_TUNING_SETTINGS } from './HiResTuningPanel';
import {
  ADAPTIVE_CAMERA_MAX_BITRATE,
  ADAPTIVE_CAMERA_MAX_FRAMERATE,
  getCameraEnableOptions,
  getCameraPublishOptions,
  getParticipantCameraProfile,
  type LiveKitCameraProfile,
} from './liveKitCameraProfiles';
import { getPublishedCameraDevice } from './cameraDeviceSelection';
import { getPreferredCameraDeviceId } from './cameraDevicePreference';
import { logVideoDiagnostic } from './videoDiagnostics';
import {
  summarizeMediaStreamTrackState,
  summarizeSenderParameters,
} from './videoTrackDiagnostics';
import { getModelRepublishReason } from './localCameraPublishProfileLogic';

type ProfiledLocalVideoTrack = LocalVideoTrack & {
  mediaStreamTrack?: MediaStreamTrack;
  sender?: RTCRtpSender;
  setDegradationPreference?: (preference: RTCDegradationPreference) => void | Promise<void>;
  setPublishingQuality?: (maxQuality: VideoQuality) => void;
};

type CameraPublicationSummaryInput = {
  trackSid?: string;
  source?: unknown;
  kind?: unknown;
  isMuted?: boolean;
  track?: unknown;
} | null | undefined;

async function applySenderProfile(
  track: ProfiledLocalVideoTrack,
  profile: LiveKitCameraProfile
) {
  const publishOptions = getCameraPublishOptions(profile, DEFAULT_HI_RES_TUNING_SETTINGS);
  const degradationPreference = publishOptions.degradationPreference ?? 'balanced';

  await track.setDegradationPreference?.(degradationPreference);
  track.setPublishingQuality?.(profile === 'model' ? VideoQuality.HIGH : VideoQuality.MEDIUM);

  const sender = track.sender;
  if (!sender) return false;

  const parameters = sender.getParameters() as RTCRtpSendParameters & {
    degradationPreference?: RTCDegradationPreference;
  };
  let changed = false;

  if (parameters.degradationPreference !== degradationPreference) {
    parameters.degradationPreference = degradationPreference;
    changed = true;
  }

  const encodings = parameters.encodings ?? [];
  const modelMaxBitrate = DEFAULT_HI_RES_TUNING_SETTINGS.liveKitMaxBitrateMbps * 1_000_000;
  const modelMaxFramerate = DEFAULT_HI_RES_TUNING_SETTINGS.liveKitMaxFramerate;

  encodings.forEach((encoding, index) => {
    const editableEncoding = encoding as RTCRtpEncodingParameters & {
      priority?: RTCPriorityType;
      networkPriority?: RTCPriorityType;
    };

    if (profile === 'model') {
      const isHighestLayer = index === encodings.length - 1;
      if (isHighestLayer && (!editableEncoding.maxBitrate || editableEncoding.maxBitrate < modelMaxBitrate)) {
        editableEncoding.maxBitrate = modelMaxBitrate;
        changed = true;
      }
      if (!editableEncoding.maxFramerate || editableEncoding.maxFramerate > modelMaxFramerate) {
        editableEncoding.maxFramerate = modelMaxFramerate;
        changed = true;
      }
      if (editableEncoding.priority !== 'high') {
        editableEncoding.priority = 'high';
        changed = true;
      }
      if (editableEncoding.networkPriority !== 'high') {
        editableEncoding.networkPriority = 'high';
        changed = true;
      }
      return;
    }

    if (!editableEncoding.maxBitrate || editableEncoding.maxBitrate > ADAPTIVE_CAMERA_MAX_BITRATE) {
      editableEncoding.maxBitrate = ADAPTIVE_CAMERA_MAX_BITRATE;
      changed = true;
    }
    if (!editableEncoding.maxFramerate || editableEncoding.maxFramerate > ADAPTIVE_CAMERA_MAX_FRAMERATE) {
      editableEncoding.maxFramerate = ADAPTIVE_CAMERA_MAX_FRAMERATE;
      changed = true;
    }
    if (editableEncoding.priority !== 'medium') {
      editableEncoding.priority = 'medium';
      changed = true;
    }
    if (editableEncoding.networkPriority !== 'medium') {
      editableEncoding.networkPriority = 'medium';
      changed = true;
    }
  });

  if (!changed) return false;

  await sender.setParameters(parameters);
  return true;
}

function summarizePublication(publication: CameraPublicationSummaryInput) {
  const track = publication?.track as ProfiledLocalVideoTrack | undefined;

  return publication
    ? {
        trackSid: publication.trackSid,
        source: publication.source,
        kind: publication.kind,
        isMuted: publication.isMuted,
        track: summarizeMediaStreamTrackState(track?.mediaStreamTrack),
        sender: summarizeSenderParameters(track?.sender),
      }
    : null;
}

async function republishCameraForProfile({
  localParticipant,
  track,
  profile,
  deviceId,
}: {
  localParticipant: LocalParticipant;
  track: ProfiledLocalVideoTrack;
  profile: LiveKitCameraProfile;
  deviceId: string | null;
}) {
  const { captureOptions, publishOptions } = getCameraEnableOptions(
    profile,
    DEFAULT_HI_RES_TUNING_SETTINGS,
    deviceId
  );

  await localParticipant.unpublishTrack(track, true);
  return localParticipant.setCameraEnabled(true, captureOptions, publishOptions);
}

export function LocalCameraPublishProfile() {
  const {
    roomCode,
    socket,
    modelParticipantId,
    modelProfileId,
    modelParticipantIds,
    participants: sessionParticipants,
    startedAt,
  } = useRoom();
  const { localParticipant } = useLocalParticipant();
  const localTracks = useTracks([Track.Source.Camera], { onlySubscribed: false });
  const localCameraTrack = localTracks.find(track => track.participant.identity === localParticipant?.identity);
  const track = localCameraTrack?.publication?.track as ProfiledLocalVideoTrack | undefined;
  const mediaStreamTrack = track?.mediaStreamTrack;
  const mediaStreamTrackId = mediaStreamTrack?.id ?? null;
  const lastAppliedProfileRef = useRef<LiveKitCameraProfile | null>(null);
  const lastCaptureRecoveryDeviceIdRef = useRef<string | null>(null);
  const emittedBackupTrackKeyRef = useRef<string | null>(null);
  const republishInProgressRef = useRef(false);
  const nextRepublishRequestIdRef = useRef(0);
  const handledRepublishRequestIdRef = useRef(0);
  const [republishRequest, setRepublishRequest] = useState<{
    id: number;
    reason: string;
    detail: unknown;
  } | null>(null);
  const participantProfileId = useMemo(() => (
    localParticipant?.identity
      ? sessionParticipants.find(participant => participant.participantId === localParticipant.identity)?.profileId ??
        sessionStorage.getItem(`participant-profileId-${roomCode}`)
      : null
  ), [localParticipant?.identity, roomCode, sessionParticipants]);
  const profile: LiveKitCameraProfile = useMemo(() => getParticipantCameraProfile({
    participantId: localParticipant?.identity,
    participantProfileId,
    modelParticipantId,
    modelProfileId,
    modelParticipantIds,
  }), [
    localParticipant?.identity,
    modelParticipantId,
    modelParticipantIds,
    modelProfileId,
    participantProfileId,
  ]);
  const diagnosticContext = useMemo(() => ({
    roomCode,
    participantId: localParticipant?.identity ?? null,
    participantName: localParticipant?.name ?? null,
    participantRole: sessionStorage.getItem(`participant-role-${roomCode}`),
  }), [localParticipant?.identity, localParticipant?.name, roomCode]);

  const emitModelTrackReady = useCallback((publication: LocalTrackPublication) => {
    if (!socket || !startedAt || !localParticipant?.identity || !publication.trackSid || publication.isMuted) return;

    const microphonePublication = localParticipant.getTrackPublication(Track.Source.Microphone);
    const audioTrackId = microphonePublication?.trackSid ?? null;
    const emitKey = `${roomCode}:${localParticipant.identity}:${publication.trackSid}:${audioTrackId ?? ''}`;
    if (emittedBackupTrackKeyRef.current === emitKey) return;

    emittedBackupTrackKeyRef.current = emitKey;
    logVideoDiagnostic('info', 'model-camera-backup-track-ready', {
      videoTrackId: publication.trackSid,
      audioTrackId,
      modelParticipantId,
      modelProfileId,
      modelParticipantIds,
      participantProfileId,
      publication: summarizePublication(publication),
    }, diagnosticContext);

    const payload = {
      videoTrackId: publication.trackSid,
      audioTrackId,
      participantId: localParticipant.identity,
    };

    socket.emit('session:model-track-ready', payload);

    [2000, 6000].forEach((delayMs) => {
      window.setTimeout(() => {
        if (emittedBackupTrackKeyRef.current !== emitKey || !socket.connected) return;

        logVideoDiagnostic('info', 'model-camera-backup-track-ready-retry', {
          delayMs,
          videoTrackId: publication.trackSid,
          audioTrackId,
          modelParticipantId,
          modelProfileId,
          modelParticipantIds,
          participantProfileId,
        }, diagnosticContext);
        socket.emit('session:model-track-ready', payload);
      }, delayMs);
    });
  }, [
    diagnosticContext,
    localParticipant,
    modelParticipantId,
    modelParticipantIds,
    modelProfileId,
    participantProfileId,
    roomCode,
    socket,
    startedAt,
  ]);

  useEffect(() => {
    const handleRepublishRequest = (event: Event) => {
      const customEvent = event as CustomEvent<{ reason?: string }>;
      const id = nextRepublishRequestIdRef.current + 1;
      nextRepublishRequestIdRef.current = id;
      setRepublishRequest({
        id,
        reason: customEvent.detail?.reason ?? 'model-camera-profile-republish-requested',
        detail: customEvent.detail ?? null,
      });
    };

    window.addEventListener('drawclub:model-camera-profile-republish-requested', handleRepublishRequest);

    return () => {
      window.removeEventListener('drawclub:model-camera-profile-republish-requested', handleRepublishRequest);
    };
  }, []);

  useEffect(() => {
    if (!localParticipant?.identity) return;
    const publication = localCameraTrack?.publication ?? localParticipant.getTrackPublication(Track.Source.Camera);

    if (!publication || publication.isMuted) {
      if (!republishInProgressRef.current) {
        lastAppliedProfileRef.current = profile;
      }
      return;
    }
    const activePublication = publication as LocalTrackPublication;

    if (!track || !mediaStreamTrack) {
      logVideoDiagnostic('warn', 'local-camera-publish-profile-track-missing', {
        profile,
        modelParticipantId,
        modelProfileId,
        modelParticipantIds,
        participantProfileId,
        publication: summarizePublication(publication),
      }, diagnosticContext);
      return;
    }

    if (mediaStreamTrack.readyState !== 'live') {
      logVideoDiagnostic('warn', 'local-camera-publish-profile-track-not-live', {
        profile,
        modelParticipantId,
        modelProfileId,
        modelParticipantIds,
        participantProfileId,
        publication: summarizePublication(publication),
      }, diagnosticContext);
      return;
    }

    let cancelled = false;
    const activeTrack = track;
    const activeMediaStreamTrack = mediaStreamTrack;
    const currentDeviceId = activeMediaStreamTrack.getSettings?.().deviceId ?? null;
    const requestedRepublishReason = profile === 'model' &&
      republishRequest &&
      republishRequest.id !== handledRepublishRequestIdRef.current
        ? republishRequest.reason
        : null;
    const republishReason = requestedRepublishReason ??
      (profile === 'model'
        ? getModelRepublishReason({
            track: activeTrack,
            lastAppliedProfile: lastAppliedProfileRef.current,
            lastCaptureRecoveryDeviceId: lastCaptureRecoveryDeviceIdRef.current,
            currentDeviceId,
          })
        : null);

    async function applyProfile() {
      const beforeTrack = summarizeMediaStreamTrackState(activeMediaStreamTrack);
      const beforeSender = summarizeSenderParameters(activeTrack.sender);
      const nextContentHint = profile === 'model' ? 'detail' : 'motion';
      if ('contentHint' in activeMediaStreamTrack) {
        activeMediaStreamTrack.contentHint = nextContentHint;
      }

      const actions: string[] = [];

      if (republishReason && !republishInProgressRef.current) {
        republishInProgressRef.current = true;
        actions.push('republish');

        const publishedDevice = getPublishedCameraDevice(localParticipant);
        const requestedDeviceId = publishedDevice.deviceId || getPreferredCameraDeviceId();
        if (
          (republishReason === 'model-camera-capture-too-small' ||
            republishReason === 'model-camera-low-resolution') &&
          currentDeviceId
        ) {
          // Record the camera we actually captured from before awaiting, so a failed
          // low-resolution recovery does not loop forever on the same device.
          lastCaptureRecoveryDeviceIdRef.current = currentDeviceId;
        }

        logVideoDiagnostic('warn', 'local-camera-profile-republish-started', {
          profile,
          reason: republishReason,
          requestedRepublish: requestedRepublishReason
            ? {
                reason: requestedRepublishReason,
                detail: republishRequest?.detail ?? null,
              }
            : null,
          modelParticipantId,
          modelProfileId,
          modelParticipantIds,
          participantProfileId,
          device: publishedDevice,
          publication: summarizePublication(publication),
          before: {
            track: beforeTrack,
            sender: beforeSender,
          },
        }, diagnosticContext);

        try {
          const nextPublication = await republishCameraForProfile({
            localParticipant,
            track: activeTrack,
            profile,
            deviceId: requestedDeviceId,
          });

          if (requestedRepublishReason && republishRequest) {
            handledRepublishRequestIdRef.current = republishRequest.id;
          }

          if (cancelled) return;

          const nextTrack = nextPublication?.track as ProfiledLocalVideoTrack | undefined;
          logVideoDiagnostic('info', 'local-camera-profile-republish-finished', {
            profile,
            reason: republishReason,
            requestedDeviceId,
            requestedDeviceLabel: publishedDevice.label,
            modelParticipantId,
            modelProfileId,
            modelParticipantIds,
            participantProfileId,
            publication: summarizePublication(nextPublication as LocalTrackPublication | null | undefined),
            after: {
              track: summarizeMediaStreamTrackState(nextTrack?.mediaStreamTrack),
              sender: summarizeSenderParameters(nextTrack?.sender),
            },
          }, diagnosticContext);
          lastAppliedProfileRef.current = profile;
          return;
        } catch (error) {
          console.warn('[LocalCameraPublishProfile] Failed to republish camera profile.', { profile, error });
          logVideoDiagnostic('error', 'local-camera-profile-republish-failed', {
            profile,
            reason: republishReason,
            error,
            modelParticipantId,
            modelProfileId,
            modelParticipantIds,
            participantProfileId,
            publication: summarizePublication(localParticipant.getTrackPublication(Track.Source.Camera)),
          }, diagnosticContext);
          return;
        } finally {
          republishInProgressRef.current = false;
        }
      }

      try {
        const changedSender = await applySenderProfile(activeTrack, profile);
        if (changedSender) actions.push('sender-parameters');
      } catch (error) {
        console.warn('[LocalCameraPublishProfile] Failed to apply sender profile.', { profile, error });
      }

      if (cancelled) return;

      logVideoDiagnostic('info', 'local-camera-publish-profile-applied', {
        profile,
        actions,
        modelParticipantId,
        modelProfileId,
        modelParticipantIds,
        participantProfileId,
        publication: summarizePublication(publication),
        before: {
          track: beforeTrack,
          sender: beforeSender,
        },
        after: {
          track: summarizeMediaStreamTrackState(activeMediaStreamTrack),
          sender: summarizeSenderParameters(activeTrack.sender),
        },
      }, {
        roomCode,
        participantId: localParticipant?.identity ?? null,
        participantName: localParticipant?.name ?? null,
        participantRole: sessionStorage.getItem(`participant-role-${roomCode}`),
      });
      lastAppliedProfileRef.current = profile;
      if (profile === 'model') {
        emitModelTrackReady(activePublication);
      }
    }

    applyProfile();

    return () => {
      cancelled = true;
    };
  }, [
    localCameraTrack?.publication,
    diagnosticContext,
    emitModelTrackReady,
    localParticipant,
    localParticipant?.identity,
    localParticipant?.name,
    mediaStreamTrack,
    mediaStreamTrackId,
    modelParticipantId,
    modelParticipantIds,
    modelProfileId,
    participantProfileId,
    profile,
    republishRequest,
    roomCode,
    startedAt,
    track,
  ]);

  return null;
}
